import Image from "next/image";
import React, { useMemo } from "react";
import BookNow from "components/BookNow/BookNow";
import Card from "components/Cards/Card";
import RateBadge from "components/Badges/Rate.badge";
import useReadMore from "hooks/useReadmore";
import { Rate, Image as ImageType } from "types/business.types";
import { ProviderUnavailable } from "types/business.types";
import { useBusinessProfile } from "hooks/useFetch";
import { toSearchEngineFriendlyWords } from "utils/utils";
import SafelySetInnerHTML from "components/Render/HTML.render";

type ReduceReturnType = {
	[K in (typeof ProviderUnavailable)[number]]?: boolean;
};

type Props = {
	id: number;
	title: string;
	description: string;
	rate: Rate;
	imageUrl: ImageType;
	unavailableItems: typeof ProviderUnavailable | undefined;
};

const DOMAIN_URL = process.env.NEXT_PUBLIC_DOMAIN_URL;

const ProviderCard = ({ id, title, description, rate, imageUrl, unavailableItems }: Props) => {
	const unavailableItem = useMemo(
		() => unavailableItems?.reduce<ReduceReturnType>((obj, cur) => ({ ...obj, [cur]: true }), {}),
		[unavailableItems]
	);
	const [truncated, hasLimit] = useReadMore(description || "", 320);
	const { data: businessProfile } = useBusinessProfile();
	const widgetSetting = businessProfile?.data.widget;

	const providerLink = `${DOMAIN_URL}/widget/${
		widgetSetting?.uniqueIdentifier
	}/provider/${toSearchEngineFriendlyWords(title)}/${id}`;

	const isImageHidden = unavailableItem?.image;
	const isRateHidden = unavailableItem?.rate;

	return (
		<Card>
			<div className="flex gap-4 sm:gap-8 items-start flex-col sm:flex-row">
				<div className="flex-auto">
					<a href={providerLink} target="_blank" className="hover:text-primary-500" rel="noreferrer">
						<h4 className="font-medium text-lg mb-1">{title}</h4>
					</a>

					{!isRateHidden && <RateBadge textClassName="text-primary-500" rate={rate.score} />}
					<p className="text-sm mt-2 text-[#46474a] leading-6 break-word">
						<SafelySetInnerHTML htmlText={truncated} />

						{hasLimit && " ..."}
					</p>
				</div>
				<div className="w-full sm:w-auto min-w-[9rem] max-w-none sm:max-w-[9rem] flex-none flex flex-col space-y-3 justify-end sm:pl-4">
					{!isImageHidden && (
						<div className="relative aspect-[1.6] sm:aspect-[1.18] overflow-hidden">
							{(imageUrl?.url && (
								<Image
									className="w-full h-full bg-gray-200 rounded-lg object-cover select-none"
									src={imageUrl?.url}
									alt={imageUrl?.alt || title}
									width={300}
									height={254}
									quality={90}
									loading="lazy"
								/>
							)) || (
								<div className="absolute inset-0 rounded-lg flex items-center justify-center bg-gray-200 text-gray-500">
									<svg
										className="w-6 h-6"
										xmlns="http://www.w3.org/2000/svg"
										fill="none"
										viewBox="0 0 32 32"
									>
										<path
											fill="currentColor"
											d="M11.8 18.75c-3.276 0-9.8 1.839-9.8 5.5V27h19.6v-2.75c0-3.661-6.524-5.5-9.8-5.5Zm-6.524 5.107a12.407 12.407 0 0 1 6.524-1.964c2.315.033 4.575.713 6.524 1.964H5.276ZM11.8 16c2.7 0 4.9-2.467 4.9-5.5S14.5 5 11.8 5s-4.9 2.467-4.9 5.5S9.1 16 11.8 16Zm0-7.857a2.237 2.237 0 0 1 2.1 2.357 2.237 2.237 0 0 1-2.1 2.357A2.237 2.237 0 0 1 9.7 10.5a2.237 2.237 0 0 1 2.1-2.357Zm9.856 10.7A6.792 6.792 0 0 1 24.4 24.25V27H30v-2.75c0-3.174-4.9-4.982-8.344-5.407ZM20.2 16c2.7 0 4.9-2.467 4.9-5.5S22.9 5 20.2 5a4.4 4.4 0 0 0-2.1.55 9.45 9.45 0 0 1 0 9.9 4.401 4.401 0 0 0 2.1.55Z"
										/>
									</svg>
								</div>
							)}
						</div>
					)}

					<BookNow uniqueIdentifier={widgetSetting?.uniqueIdentifier} type="provider" id={id} buttonWide />
				</div>
			</div>
		</Card>
	);
};

export default ProviderCard;
